'use client';

import { CircleAlert } from 'lucide-react';
import { useId, type ReactNode } from 'react';

import { Label } from '@platform/ui/components/label';
import { cn } from '@platform/ui/lib/utils';

export interface FieldProps {
    label: ReactNode;
    /** Explains the field; hidden while an error takes its place. */
    hint?: ReactNode;
    /** The server's word for what is wrong. Its presence marks the control invalid. */
    error?: ReactNode;
    required?: boolean;
    className?: string;
    children: (control: { id: string; describedBy?: string; invalid: boolean }) => ReactNode;
}

/**
 * A label 8px above its control, a hint 4px below it and an error in the hint's
 * place (docs/10 §4, docs/11 §3) — the hint sits closer so it reads as this
 * field's note, not the start of the next field. The ids that tie them together
 * reach the control through the render prop, so the relationship cannot be
 * forgotten at a call site. The error carries an icon, so it does not rely on
 * colour alone, and is announced.
 */
export function Field({ label, hint, error, required, className, children }: FieldProps) {
    const id = useId();
    const errorId = `${id}-error`;
    const hintId = `${id}-hint`;
    const describedBy = error ? errorId : hint ? hintId : undefined;

    return (
        <div data-slot="field" className={cn('flex flex-col gap-1', className)}>
            <Label htmlFor={id} className="mb-1">
                {label}
                {required && (
                    <span aria-hidden="true" className="ms-1 text-destructive-strong">
                        *
                    </span>
                )}
            </Label>
            {children({ id, describedBy, invalid: Boolean(error) })}
            {error ? (
                <p id={errorId} role="alert" className="flex items-start gap-2 text-13 text-destructive-strong">
                    <CircleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                    <span>{error}</span>
                </p>
            ) : hint ? (
                <p id={hintId} className="text-13 text-muted-foreground">
                    {hint}
                </p>
            ) : null}
        </div>
    );
}
